import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, ChevronRight } from 'lucide-react';

const SCRIPTS = {
  student: {
    greeting: "Hi! I'm your Futurum guide 👋 I'm here to help you navigate the platform as a student. What would you like to do?",
    quickReplies: ['Browse topics', 'Apply to a topic', 'Team projects', 'Post my own idea', 'Track my applications', 'Submit a project', 'View my profile'],
    responses: {
      'browse topics': {
        text: "Go to **Marketplace** in the sidebar. You'll see all approved research topics from lecturers and alumni. Topics with a gold **Team project** badge are open for team applications. You can filter by domain, status, or search by keyword.",
        replies: ['Apply to a topic', 'Team projects', 'Post my own idea']
      },
      'apply to a topic': {
        text: "In the **Marketplace**, click on any topic card. For solo topics, click **Apply** and fill in your statement of interest, relevant experience, and proposed approach. For team topics, click **Apply as Team** instead — see 'Team projects' for the full flow.",
        replies: ['Team projects', 'Track my applications', 'Browse topics']
      },
      'team projects': {
        text: "Some topics are designated as **team projects** — shown with a gold badge. If the topic uses pre-formed teams, click **Apply as Team**, enter a team name, then add your teammate by their registered email address. They'll receive an invitation to accept or decline.",
        replies: ['How do I invite a teammate?', 'What if my teammate declines?', 'What can team members do?', 'Browse topics']
      },
      'how do i invite a teammate?': {
        text: "In the **Apply as Team** modal, type your teammate's registered Futurum email address in the invite field and click **Add**. Once all teammates are added, click **Create Team and Send Invitations**. They'll each get a platform notification and email.",
        replies: ['What happens after they accept?', 'What if my teammate declines?', 'Team projects']
      },
      'what happens after they accept?': {
        text: "Once all teammates accept, you'll see a notification: 'All team members have accepted — you can now submit your application.' Return to the **Marketplace**, click **Apply as Team** again — the modal will skip straight to the application form.",
        replies: ['Track my applications', 'What can team members do?', 'Team projects']
      },
      'what if my teammate declines?': {
        text: "If a teammate declines, you'll be notified. You can invite a replacement student (if you're under the maximum team size) or proceed with the remaining accepted members. Just open the Apply as Team modal and add a new teammate.",
        replies: ['How do I invite a teammate?', 'Team projects']
      },
      'what can team members do?': {
        text: "All team members can view the project and milestone progress. However, only the **team lead** can submit milestones, upload files, and submit the final project. If you are a team member (not the lead), your milestone controls will be read-only.",
        replies: ['Team projects', 'Submit a project']
      },
      'where do i accept a team invitation?': {
        text: "Check your **Dashboard Home** — you'll see a gold **Pending Team Invitations** card at the top. You can also find it on your **My Projects** page. Click **Accept** or **Decline** directly from either card.",
        replies: ['What happens after they accept?', 'Team projects']
      },
      'post my own idea': {
        text: "Go to **Post a Topic** in the sidebar. Describe your research idea, select a domain, and add your skills. Once submitted, the admin will review and approve it — after which alumni can discover and sponsor it.",
        replies: ['Track my applications', 'Browse topics']
      },
      'track my applications': {
        text: "Go to **My Applications** in the sidebar. You'll see all your submitted applications and their current status — pending, accepted, or rejected. Team applications are labelled with a **team** badge.",
        replies: ['Submit a project', 'Browse topics']
      },
      'submit a project': {
        text: "Go to **My Projects** in the sidebar. If your application has been accepted, you'll see your active project there. If you are the team lead, you can submit milestones and the final deliverable from that page.",
        replies: ['What can team members do?', 'Track my applications']
      },
      'view my profile': {
        text: "Click **Profile** in the sidebar. You can update your matric number, department, GPA, skills, and bio. A complete profile helps lecturers and sponsors evaluate your application.",
        replies: ['Apply to a topic', 'Browse topics']
      },
      'forgot password': {
        text: "On the login page, click the **Forgot password?** link below the password field. Enter your registered email address and we'll send you a reset link. The link expires in 1 hour. Check your spam folder if it doesn't arrive.",
        replies: ['Browse topics']
      },
    }
  },

  alumni: {
    greeting: "Hi! I'm your Futurum guide 👋 I'm here to help you as an alumni sponsor. What would you like to do?",
    quickReplies: ['Sponsor a project', 'Post a topic', 'View my sponsorships', 'Upload bank receipt', 'View Hall of Honour', 'Select an awardee'],
    responses: {
      'sponsor a project': {
        text: "Go to **Marketplace** in the sidebar. Browse available topics and click **Sponsor** on any one that interests you. Select an amount (₦100k–₦300k), add your selection criteria, and optionally upload your bank transfer receipt.",
        replies: ['Upload bank receipt', 'View my sponsorships']
      },
      'post a topic': {
        text: "Go to **Post a Topic** in the sidebar. Describe the research problem you want students to work on, set the domain and required skills, and submit. The admin will review and approve it. You can also enable team project mode if the work suits a collaborative approach.",
        replies: ['Sponsor a project', 'View my sponsorships']
      },
      'view my sponsorships': {
        text: "Go to **My Sponsorships** in the sidebar. You'll see all your active and past sponsorships, their status, and the applicants for each sponsored topic.",
        replies: ['Select an awardee', 'Upload bank receipt']
      },
      'upload bank receipt': {
        text: "When sponsoring a project in the **Marketplace**, you'll see a file upload field for your bank transfer receipt. Upload your PDF or image — the admin will be notified automatically to confirm receipt of funds.",
        replies: ['View my sponsorships']
      },
      'view hall of honour': {
        text: "The **Hall of Honour** is a public page showing all alumni donors ranked by total giving. You can visit it at futurumawards.com/honour. Your name appears there once your sponsorship is confirmed.",
        replies: ['Sponsor a project']
      },
      'select an awardee': {
        text: "Go to **My Sponsorships**, click on an active sponsorship, and you'll see a list of applicants. For team topics, applications appear as team cards — accepting one card accepts all team members simultaneously and creates the project. For solo topics, click **Select** on your preferred candidate.",
        replies: ['View my sponsorships']
      },
      'forgot password': {
        text: "On the login page, click the **Forgot password?** link below the password field. Enter your registered email and we'll send you a reset link. It expires in 1 hour.",
        replies: ['Sponsor a project']
      },
    }
  },

  lecturer: {
    greeting: "Hi! I'm your Futurum guide 👋 I'm here to help you as a lecturer. What would you like to do?",
    quickReplies: ['Submit a topic', 'Create a team topic', 'View my topics', 'Review applications', 'Track student projects', 'Update my profile'],
    responses: {
      'submit a topic': {
        text: "Go to **My Topics** in the sidebar and click **Submit Topic**. Fill in the title, domain, description, required skills, and number of student slots. You can also enable team project mode at the bottom of the form. Once submitted, the admin will review and approve it.",
        replies: ['Create a team topic', 'View my topics', 'Review applications']
      },
      'create a team topic': {
        text: "When filling in the topic submission form, scroll to the **Team project settings** section at the bottom and toggle it on. Choose the application mode: **Pre-formed** (students self-organise before applying) or **Individual** (you or the sponsor assembles the team from applicants). You can also set a maximum team size.",
        replies: ['How do I approve a team application?', 'View my topics']
      },
      'how do i approve a team application?': {
        text: "Go to **Applications** and find the topic. Team applications show one card per member. Click **Review** on the lead student's card and select **Accept** — this automatically accepts all team members simultaneously and creates the project. You do not need to approve each member separately.",
        replies: ['Track student projects', 'Review applications']
      },
      'view my topics': {
        text: "Go to **My Topics** in the sidebar. You'll see all topics you've submitted along with their approval status, number of applicants, and funding information. Topics with team mode enabled show a **Team project** badge.",
        replies: ['Review applications', 'Submit a topic']
      },
      'review applications': {
        text: "Go to **Applications** in the sidebar. You'll see all student applications to your topics. For team topics, applications appear grouped by team. Click **Review** on any application to approve or decline it.",
        replies: ['How do I approve a team application?', 'Track student projects']
      },
      'track student projects': {
        text: "Go to **Projects** in the sidebar. You'll see all active projects linked to your topics — including team projects with their full member list. You can monitor milestone submissions and update project status.",
        replies: ['Review applications']
      },
      'update my profile': {
        text: "Click **Profile** in the sidebar. Add your title, department, faculty, research areas, and bio. A complete profile helps students and alumni understand your research focus.",
        replies: ['Submit a topic']
      },
      'forgot password': {
        text: "On the login page, click the **Forgot password?** link below the password field. Enter your registered email and we'll send you a reset link. It expires in 1 hour.",
        replies: ['Submit a topic']
      },
    }
  },

  judge: {
    greeting: "Hi! I'm your Futurum guide 👋 I'm here to help you as a judge. What would you like to do?",
    quickReplies: ['View assigned projects', 'Submit a score', 'View rankings', 'Edit a score'],
    responses: {
      'view assigned projects': {
        text: "Go to **Assigned Projects** in the sidebar. You'll see all projects assigned to you for evaluation, along with their current scoring status. Team projects will show all team members.",
        replies: ['Submit a score', 'View rankings']
      },
      'submit a score': {
        text: "In **Assigned Projects**, click on a project to view its full details — milestones, student profile, and topic description. Use the scoring sliders to evaluate each criterion and submit your score.",
        replies: ['View rankings', 'Edit a score']
      },
      'view rankings': {
        text: "Go to **Rankings** in the sidebar. You'll see the current project standings based on all submitted judge scores.",
        replies: ['Submit a score']
      },
      'edit a score': {
        text: "Go to **Assigned Projects** and click on a project you've already scored. You'll see an **Edit Score** option to revise your evaluation before the judging period closes.",
        replies: ['View rankings']
      },
      'forgot password': {
        text: "On the login page, click the **Forgot password?** link below the password field. Enter your registered email and we'll send you a reset link. It expires in 1 hour.",
        replies: ['View assigned projects']
      },
    }
  },

  admin: {
    greeting: "Hi! I'm your Futurum guide 👋 I'm here to help you manage the platform. What would you like to do?",
    quickReplies: ['Approve a topic', 'Manage users', 'Configure programme', 'Assign judges', 'View analytics'],
    responses: {
      'approve a topic': {
        text: "Go to **Topics** in the sidebar. You'll see all topics pending review. Click **Approve** to publish a topic to the marketplace, or **Request Revision** to send feedback to the submitter. Team-enabled topics will show their application mode.",
        replies: ['Manage users', 'View analytics']
      },
      'manage users': {
        text: "Go to **Users** in the sidebar. You can view all registered users, filter by role and status, approve pending accounts, or suspend users if needed.",
        replies: ['Assign judges', 'Configure programme']
      },
      'configure programme': {
        text: "Go to **Program Config** in the sidebar. Set registration deadlines, application windows, submission dates, and toggle registration open/closed.",
        replies: ['Manage users', 'Assign judges']
      },
      'assign judges': {
        text: "Go to **Judging** in the sidebar. From there you can assign approved judge users to specific projects for evaluation.",
        replies: ['View analytics']
      },
      'view analytics': {
        text: "Go to **Analytics** in the sidebar for a full overview — user growth, topic submissions, application rates, funding totals, and project progress.",
        replies: ['Configure programme']
      },
      'forgot password': {
        text: "On the login page, click the **Forgot password?** link below the password field. Enter your registered email and we'll send you a reset link. It expires in 1 hour.",
        replies: ['Manage users']
      },
    }
  },
};

export default function Chatbot({ userRole }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const bottomRef = useRef(null);
  const script = SCRIPTS[userRole] || SCRIPTS.student;

  useEffect(() => {
    if (open && !initialized) {
      setMessages([{ from: 'bot', text: script.greeting, replies: script.quickReplies }]);
      setInitialized(true);
    }
  }, [open, initialized, script]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleReply = (reply) => {
    const key = reply.toLowerCase();
    const response = script.responses[key];
    setMessages(prev => [
      ...prev,
      { from: 'user', text: reply },
      response
        ? { from: 'bot', text: response.text, replies: response.replies }
        : { from: 'bot', text: "I don't have specific guidance on that yet. Try browsing the sidebar or contact the admin at admin@futurumawards.com.", replies: script.quickReplies }
    ]);
  };

  const renderText = (text) => {
    return text.split(/\*\*(.*?)\*\*/g).map((part, i) =>
      i % 2 === 1 ? <strong key={i} style={{ color: 'var(--gold)' }}>{part}</strong> : part
    );
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 500,
          width: 52, height: 52, borderRadius: '50%',
          background: 'var(--gold)', color: '#0a0c14',
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(201,162,39,0.4)',
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        title="Platform Guide"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </button>

      {/* Chat window */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 92, right: 28, zIndex: 500,
          width: 340, maxHeight: 500,
          background: 'var(--surface-card)',
          border: '1px solid rgba(201,162,39,0.25)',
          borderRadius: 16, overflow: 'hidden',
          boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
          display: 'flex', flexDirection: 'column',
          animation: 'slideUp 0.2s ease',
        }}>
          {/* Header */}
          <div style={{ padding: '14px 16px', background: 'var(--gold)', color: '#0a0c14', display: 'flex', alignItems: 'center', gap: 10 }}>
            <MessageCircle size={18} />
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Futurum Guide</div>
              <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>Here to help you navigate</div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {messages.map((msg, i) => (
              <div key={i}>
                <div style={{
                  maxWidth: '85%',
                  marginLeft: msg.from === 'user' ? 'auto' : 0,
                  background: msg.from === 'user' ? 'rgba(201,162,39,0.15)' : 'rgba(128,128,128,0.08)',
                  border: msg.from === 'user' ? '1px solid rgba(201,162,39,0.25)' : '1px solid var(--border-card)',
                  borderRadius: msg.from === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                  padding: '10px 12px',
                  fontSize: '0.85rem',
                  color: 'var(--text-primary)',
                  lineHeight: 1.5,
                }}>
                  {msg.from === 'bot' ? renderText(msg.text) : msg.text}
                </div>
                {msg.replies && (
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {msg.replies.map(r => (
                      <button
                        key={r}
                        onClick={() => handleReply(r)}
                        style={{
                          padding: '5px 10px', borderRadius: 999, fontSize: '0.75rem',
                          background: 'transparent', border: '1px solid rgba(201,162,39,0.35)',
                          color: 'var(--gold)', cursor: 'pointer',
                          transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 4
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(201,162,39,0.1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <ChevronRight size={10} /> {r}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        </div>
      )}
    </>
  );
}
